<div class="dash-shell">
  <header class="page-head">
    <nav class="page-head__crumbs" aria-label="Breadcrumb">
      <a class="page-head__crumb" routerLink="/dashboard">Home</a>
      <span class="page-head__sep" aria-hidden="true">/</span>
      @if (entity(); as e) {
        <a class="page-head__crumb" [routerLink]="['/entity', entityId, 'dashboard']">{{
          e.name
        }}</a>
        <span class="page-head__sep" aria-hidden="true">/</span>
      }
      <span class="page-head__crumb page-head__crumb--current" aria-current="page">Apply</span>
    </nav>
    <div class="page-head__bar">
      <div class="page-head__titles">
        <h1 class="page-head__title">Apply for funding</h1>
        <p class="page-head__subtitle">
          Share what you have and submit. We'll review your details and come back with the right
          funding for your business.
        </p>
      </div>
      @if (reviewStatus(); as status) {
        <app-status-chip [status]="status" data-testid="preapp-status-chip" />
      }
    </div>
  </header>

  @if (loading()) {
    <!-- ===== Initial state fetch ===== -->
    <div class="pre-app__loading" role="status" aria-live="polite" data-testid="preapp-loading">
      <mat-spinner diameter="28" />
      <span>Loading…</span>
    </div>
  } @else if (forbidden()) {
    <!-- ===== No authority for this business ===== -->
    <div
      class="pre-app pre-app__state pre-app__state--blocked"
      role="alert"
      data-testid="preapp-forbidden"
    >
      <span class="pre-app__state-icon pre-app__state-icon--blocked" aria-hidden="true">
        <app-icon name="shield" [size]="32" />
      </span>
      <h2 class="pre-app__state-title">You can't act for this business</h2>
      <p class="pre-app__state-body">
        Your account doesn't have permission to apply for funding on behalf of this business. If you
        think this is wrong, ask a business admin to invite you, or pick a business you manage from
        your dashboard.
      </p>
      <button
        mat-flat-button
        color="primary"
        type="button"
        (click)="goToDashboard()"
        data-testid="forbidden-dashboard-btn"
      >
        Back to dashboard
      </button>
    </div>
  } @else if (referred()) {
    <!-- ===== Referred at the door (§A1.4, FLOW-192 outcome 2) =====
         A trust, an NPO or a business with no CIPC registration is handled by a
         person, not by the system. One leading message, what happens next, and no
         document step, no submit, no product and no figure (§A12, N4). -->
    <div class="pre-app pre-app__state" role="status" data-testid="preapp-referred">
      <span class="pre-app__state-icon pre-app__state-icon--go" aria-hidden="true">
        <app-icon name="users" [size]="32" />
      </span>
      <h2 class="pre-app__state-title" data-testid="preapp-referred-title">
        {{ referralCopy()?.headline || 'A CapX person will be in touch' }}
      </h2>
      @if (referralCopy()?.reason; as reason) {
        <p class="pre-app__state-body" data-testid="preapp-referred-body">{{ reason }}</p>
      }
      <section class="next-steps pre-app__state-next" aria-label="What happens next">
        <h3 class="next-steps__title">What happens next</h3>
        <ul class="next-steps__list">
          <li>
            <app-icon name="phone" [size]="16" />
            <span>Someone from CapX contacts you to talk through what your business needs.</span>
          </li>
          <li>
            <app-icon name="check" [size]="16" />
            <span>Nothing more is needed from you right now.</span>
          </li>
        </ul>
      </section>
      <button
        mat-stroked-button
        type="button"
        (click)="goToDashboard()"
        data-testid="referred-dashboard-btn"
      >
        Back to dashboard
      </button>
    </div>
  } @else if (reviewStatus() === 'SUBMITTED') {
    <!-- ===== Under review — the confirmation's final state (FLOW-246 replays into it) ===== -->
    <div class="pre-app pre-app__state" data-testid="preapp-submitted">
      <app-submitted-status
        [dfIntent]="dfIntent()"
        [hasAgeAnalysis]="dfUnlocked()"
        [documentCount]="attachedDocs().length"
        (dashboard)="goToDashboard()"
        (addDocuments)="goToAddDocuments()"
      />
    </div>
  } @else if (reviewStatus() === 'PROCEEDED') {
    <!-- ===== Proceeded — CapX opened the product application(s) ===== -->
    <div class="pre-app pre-app__state" role="status" data-testid="preapp-proceeded">
      <span class="pre-app__state-icon pre-app__state-icon--go" aria-hidden="true">
        <app-icon name="check-circle" [size]="32" />
      </span>
      <h2 class="pre-app__state-title">Good news — your application is open</h2>
      <p class="pre-app__state-body">
        CapX has opened the right funding application for your business. Continue it from your
        dashboard, where you'll find it under your recent applications.
      </p>
      <button
        mat-flat-button
        color="primary"
        type="button"
        (click)="goToDashboard()"
        data-testid="proceeded-dashboard-btn"
      >
        View my applications
      </button>
    </div>
  } @else {
    <!-- ===== Intake wizard (OPEN, or reopened after DECLINED) ===== -->
    @if (nameToConfirm(); as name) {
      <app-company-name-check [name]="name" (answered)="answerNameCheck($event)" />
    }
    @if (serverError()) {
      <div class="pre-app__error" role="alert" data-testid="preapp-error">{{ serverError() }}</div>
    }

    @if (reopened()) {
      <div
        class="pre-app__notice"
        [class.pre-app__notice--declined]="reviewStatus() === 'DECLINED'"
        role="status"
        data-testid="preapp-reopened-notice"
      >
        <h2 class="pre-app__notice-title" data-testid="preapp-notice-title">
          We couldn't proceed just yet
        </h2>
        @if (clientReason()) {
          <p class="pre-app__notice-body" data-testid="preapp-notice-reason">
            {{ clientReason() }}
          </p>
        }
        <p class="pre-app__notice-hint">
          @if (startedAt()) {
            <span data-testid="preapp-notice-age"
              >You started this application {{ startedAt() | ago }}.</span
            >
          }
          Nothing here is final — update your details below and submit again whenever things change.
        </p>
      </div>
    }

    @if (reviewStatus() === 'OPEN' && startedAt()) {
      <p class="pre-app__resumed" role="status" data-testid="preapp-resumed">
        <app-icon name="info" [size]="16" />
        <span>
          You started this application {{ startedAt() | ago }}. Everything you've added is saved —
          pick up where you left off.
        </span>
      </p>
    }

    <!-- Progress header — bar + numbered steps. -->
    <section class="stepper" aria-label="Application progress">
      <div class="stepper__top">
        <div class="stepper__label">
          <span class="stepper__count">Step {{ currentIndex() + 1 }} of {{ steps.length }}</span>
          <span class="stepper__name">{{ steps[currentIndex()].label }}</span>
        </div>
      </div>
      <div
        class="stepper__bar"
        role="progressbar"
        [attr.aria-valuenow]="progressPct()"
        aria-valuemin="0"
        aria-valuemax="100"
      >
        <span class="stepper__bar-fill" [style.width.%]="progressPct()"></span>
      </div>
      <ol class="stepper__steps">
        @for (s of steps; track s.id; let i = $index) {
          <li
            class="stepper__step"
            [class.stepper__step--done]="stepState(s.id) === 'done'"
            [class.stepper__step--active]="stepState(s.id) === 'active'"
            [attr.aria-current]="stepState(s.id) === 'active' ? 'step' : null"
          >
            <button
              type="button"
              class="stepper__marker"
              [disabled]="stepState(s.id) !== 'done'"
              (click)="goToStep(s.id)"
            >
              @if (stepState(s.id) === 'done') {
                <app-icon name="check" [size]="16" />
              } @else {
                {{ i + 1 }}
              }
            </button>
            <span class="stepper__step-label">{{ s.label }}</span>
          </li>
        }
      </ol>
    </section>

    <section class="pre-app" aria-label="Apply for funding">
      @if (verdictPollTimedOut()) {
        <p class="pre-app__checking" role="status" data-testid="verdict-timeout">
          <app-icon name="info" [size]="16" />
          <span>
            Some documents are still being checked — this can take a little longer at busy times.
            You can keep going; we'll finish the checks in the background.
          </span>
        </p>
      }

      <!--
        The AI-processing consent. Defined once and rendered on BOTH upload steps:
        the gate is on every document POST, so a client who starts on step 2 must be
        able to consent there too rather than meeting a refusal with no box on screen.
      -->
      <ng-template #aiConsentBlock>
        <!--
          Since the sign-up capture (2026-09-22) the consent is a standing one
          from the register form, and this box is the FALLBACK: rendered only
          when no standing consent exists or the consent on this intake was
          withdrawn. On the standing path the server derives the intake's
          consent and there is nothing to ask — only the withdrawal below.
        -->
        @if (showAiConsentBox()) {
          <div class="pre-app__consent" data-testid="preapp-ai-consent">
            <app-ai-consent-checkbox
              testId="preapp-ai-consent-checkbox"
              [checked]="aiConsent()"
              [disabled]="consentRecorded()"
              (changed)="onAiConsentChange($event)"
            />

            @if (consentBlocked()) {
              <p class="pre-app__consent-error" role="alert" data-testid="preapp-ai-consent-error">
                <app-icon name="shield" [size]="16" />
                <span>
                  Tick the box above before uploading — we can’t check your documents without your
                  consent.
                </span>
              </p>
            }
          </div>
        }
      </ng-template>

      <!-- ===== STEP 1 — Bank statements (stays mounted; shows itself while active) ===== -->
      <app-bank-accounts-step
        [active]="step() === 'bank'"
        [entityId]="entityId"
        [preAppId]="preAppId()"
        [uploads]="bankUploads()"
        [deletingIds]="deletingIds()"
        [faces]="faces()"
        [(attributions)]="attributions"
        [verdictPollTimedOut]="verdictPollTimedOut()"
        [consentBlock]="aiConsentBlock"
        (filesPicked)="onFilesPicked('BANK_STATEMENT', $event)"
        (remove)="removeUpload($event)"
        (next)="next()"
        (documentsChanged)="refreshDocuments()"
      />

      <!-- ===== Encouragement intercept — Step 2 skipped with nothing uploaded ===== -->
      @if (intercept()) {
        <app-encourage-intercept
          (submitWithout)="submitWithoutFinancials()"
          (uploadFinancials)="uploadFinancials()"
        />
      }

      <!-- ===== STEP 2 — Financial information ===== -->
      @if (step() === 'financials' && !intercept()) {
        <div class="pre-app__panel">
          <div class="step-head">
            <h2 class="step-head__title">Financial Information</h2>
            <p class="step-head__lead">
              Optional, but recommended — sharing these strengthens your application and can improve
              your offer.
            </p>
          </div>

          <ng-container [ngTemplateOutlet]="aiConsentBlock" />

          @for (doc of FINANCIAL_DOCS; track doc.type.value) {
            <div
              class="checklist__row"
              [class.checklist__row--done]="statusFor(doc.type.value) === 'added'"
            >
              <div class="checklist__row-head">
                <span class="checklist__row-title">{{ doc.type.label }}</span>
                <span class="checklist__tag checklist__tag--optional">{{
                  requirementLabel(doc.requirement)
                }}</span>
              </div>
              <p class="checklist__why">{{ doc.why }}</p>
              <app-doc-drop
                [docType]="doc.type"
                [uploads]="uploadsByType().get(doc.type.value) ?? []"
                [deletingIds]="deletingIds()"
                (filesPicked)="onFilesPicked(doc.type.value, $event)"
                (remove)="removeUpload($event)"
              />
            </div>
          }

          <div class="pre-app__nav">
            <button mat-stroked-button type="button" (click)="back()" data-testid="step-fin-back">
              <app-icon name="arrow-left" [size]="18" />
              <span>Back</span>
            </button>
            @if (hasFinancialDocs()) {
              <button
                mat-flat-button
                color="primary"
                type="button"
                (click)="next()"
                [disabled]="anyInFlight()"
                data-testid="step-fin-next"
                data-variant="continue"
              >
                <span>Continue to summary</span>
                <app-icon name="arrow-right" [size]="18" />
              </button>
            } @else {
              <!-- Nothing shared on this step: the forward action is a skip
                   (link-styled, as the master document has it) and leads to the
                   encouragement intercept, not straight to the summary. -->
              <button
                mat-button
                type="button"
                class="pre-app__skip"
                (click)="next()"
                [disabled]="anyInFlight()"
                data-testid="step-fin-next"
                data-variant="skip"
              >
                <span>Skip this step</span>
                <app-icon name="arrow-right" [size]="18" />
              </button>
            }
          </div>
        </div>
      }

      <!-- ===== STEP 3 — Application summary ===== -->
      @if (step() === 'offers') {
        <app-summary-step
          [uploads]="uploads()"
          [hasFinancialDocs]="hasFinancialDocs()"
          [dfUnlocked]="dfUnlocked()"
          [reopened]="reopened()"
          [canSubmit]="canSubmit()"
          [isSubmitting]="isSubmitting()"
          [submittingTrack]="submittingTrack()"
          [anyInFlight]="anyInFlight()"
          (back)="back()"
          (jumpTo)="goToStep($event)"
          (submitTrack)="submit($event)"
        />
      }
    </section>
  }
</div>
